import { Modal } from '@startuptools/ui/Modal';
import { ModalDialog } from '@startuptools/ui/ModalDialog';
import { ModalClose } from '@startuptools/ui/ModalClose';
import { DisplayIdentity, Identity } from '@startuptools/business-ui/identity/Identity';
import { DialogTitle } from '@startuptools/ui/joy/DialogTitle';
import { DialogActions } from '@startuptools/ui/joy/DialogActions';
import { DialogContent } from '@startuptools/ui/joy/DialogContent';
import { Trans, useLingui } from '@lingui/react';
import { Select } from '@startuptools/ui/Select';
import { NumericFormatInput } from '@startuptools/ui/NumericFormatInput';
import { Stack } from '@startuptools/ui/Stack';
import { Button } from '@startuptools/ui/Button';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { GqlShareClass, GqlShareIssue, GqlShareIssueParticipant } from '../../../graphql/base-types.graphql';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Option } from '@startuptools/ui/Option';
import { useMemo } from 'react';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { useCreateShareIssueParticipantMutation, useSipEditMutation } from '../../../graphql/react-operations';
import { ShareIssueDocument } from '../ShareIssue.graphql';
import { shareClassToInput } from '../../../helpers/share-class-to-input';
import { captureException } from '@sentry/react';
import { genericErrorToast } from '@startuptools/ui/toast/use-toast';
import { identityToInput } from '@startuptools/business-ui/identity/identity-to-input';
import { PaymentKind } from '@startuptools/common/participants';
import { BigNumber } from 'bignumber.js';
import { claimRecalc } from '../../../common/claim-recalc';
import { useFieldSync } from '@startuptools/common/react-hooks/use-field-sync';

const calcNumberOfSharesByClaim = (claim: string, pricePerWarrant: BigNumber, sharesLeftToDistribute: number) => {
  const { newClaim, newUnits } = claimRecalc(new BigNumber(claim), pricePerWarrant, sharesLeftToDistribute);
  return { newClaim: newClaim.toString(), newUnits: newUnits.toString() };
};

const calcClaimByNumberOfShares = (quantity: string, pricePerWarrant: BigNumber) => {
  const claim = pricePerWarrant.times(quantity);
  return { newClaim: claim.toString() };
};

type FormValues = Pick<GqlShareIssueParticipant, 'claim'> & {
  shareClassName: string;
  numberOfShares: string;
  claim: string | undefined;
};

export const ShareIssueParticipantDialog = ({
  sips,
  sip,
  onClose,
  shareIssue,
  paymentKind,
  sirId,
}: {
  sips: Pick<GqlShareIssueParticipant, 'numberOfShares' | 'shareClass'>[];
  sip: Pick<GqlShareIssueParticipant, 'numberOfShares' | 'claim'> & {
    id: string | null | undefined;
    shareClass: GqlShareClass | null | undefined;
  } & {
    identity: DisplayIdentity;
  };
  onClose: () => void;
  shareIssue: Pick<GqlShareIssue, 'shareClasses' | 'companyId' | 'pricePerShare'>;
  paymentKind: PaymentKind;
  sirId: string;
}) => {
  const { i18n } = useLingui();
  const form = useForm<FormValues>({
    defaultValues: {
      shareClassName: sip.shareClass?.name ?? shareIssue.shareClasses.at(0)?.name,
      numberOfShares: sip.numberOfShares ?? undefined,
      claim: sip.claim ?? undefined,
    },
  });

  const [doCreate, { loading: createLoading }] = useCreateShareIssueParticipantMutation({
    refetchQueries: [ShareIssueDocument],
    awaitRefetchQueries: true,
  });
  const [doUpdate, { loading: updateLoading }] = useSipEditMutation({
    refetchQueries: [ShareIssueDocument],
    awaitRefetchQueries: true,
  });

  const loading = createLoading || updateLoading;
  const currentShareClassName = form.watch('shareClassName');

  const sharesLeftToDistribute = useMemo(() => {
    const allocated = sips.reduce((sum, sip) => {
      if (!sip.shareClass || !sip.numberOfShares || sip.shareClass.name !== currentShareClassName) {
        return sum;
      }
      return sum + Number(sip.numberOfShares);
    }, 0);
    const currentNos = Number(sip.numberOfShares ?? 0);
    const currentSc = shareIssue.shareClasses.find(sc => sc.name === currentShareClassName);
    if (!currentSc) {
      return 0;
    }
    return Number(currentSc.max) - allocated + currentNos;
  }, [sip.numberOfShares, currentShareClassName, shareIssue.shareClasses, sips]);

  useFieldSync({
    form,
    enabled: paymentKind === PaymentKind.Offset,
    fields: {
      claim: value => {
        if (!value) return {};
        const { newUnits } = calcNumberOfSharesByClaim(
          value,
          new BigNumber(shareIssue.pricePerShare!),
          sharesLeftToDistribute,
        );
        return { numberOfShares: newUnits };
      },
      numberOfShares: value => {
        if (!value) return {};
        const { newClaim } = calcClaimByNumberOfShares(value, new BigNumber(shareIssue.pricePerShare!));
        return { claim: newClaim };
      },
    },
  });

  const onSubmit: SubmitHandler<FormValues> = async values => {
    try {
      const shareClass = shareClassToInput(shareIssue.shareClasses.find(sc => sc.name === values.shareClassName)!);
      if (sip.id) {
        await doUpdate({
          variables: {
            companyId: shareIssue.companyId,
            input: {
              id: sip.id,
              shareClass,
              numberOfShares: values.numberOfShares,
              claim: paymentKind === PaymentKind.Offset ? values.claim! : null,
            },
          },
        });
      } else {
        await doCreate({
          variables: {
            companyId: shareIssue.companyId,
            input: {
              identityInput: identityToInput(sip.identity),
              paymentKind,
              shareClass,
              numberOfShares: values.numberOfShares,
              shareIssueRegistrationId: sirId,
              claim: paymentKind === PaymentKind.Offset ? values.claim! : null,
            },
          },
        });
      }
      onClose();
    } catch (e) {
      captureException(e);
      genericErrorToast();
    }
  };

  return (
    <Modal open onClose={onClose}>
      <ModalDialog>
        <ModalClose />
        <DialogTitle>{sip.id ? <Trans id="Edit Subscriber" /> : <Trans id="Add Subscriber" />}</DialogTitle>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <DialogContent>
            <Stack gap={3} my={3}>
              <Identity identity={sip.identity} editable={false} />

              <Controller
                control={form.control}
                name="shareClassName"
                render={({ field }) => (
                  <FormControl required>
                    <FormLabel>
                      <Trans id="Share Class" />
                    </FormLabel>
                    <Select {...field} required>
                      {shareIssue.shareClasses.map(sc => (
                        <Option value={sc.name} key={sc.name}>
                          <Trans
                            id="{shareClass} (vote weight: {voteWeight})"
                            values={{ shareClass: sc.name, voteWeight: sc.voteWeight }}
                          />
                        </Option>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />

              <Controller
                control={form.control}
                name="numberOfShares"
                render={({ field }) => (
                  <FormControl>
                    <FormLabel required>
                      <Trans id="Number of Shares" />
                    </FormLabel>
                    <NumericFormatInput
                      {...field}
                      required
                      endDecorator={
                        <Button
                          variant="soft"
                          color="neutral"
                          onClick={() => {
                            form.setValue('numberOfShares', sharesLeftToDistribute.toString());
                          }}
                        >
                          <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                            {sharesLeftToDistribute}
                          </NumberFormat>
                        </Button>
                      }
                    />
                  </FormControl>
                )}
              />

              {paymentKind === PaymentKind.Offset && (
                <Controller
                  control={form.control}
                  name="claim"
                  render={({ field }) => (
                    <FormControl>
                      <FormLabel required>
                        <Trans id="Claim" />
                      </FormLabel>
                      <NumericFormatInput {...field} required />
                    </FormControl>
                  )}
                />
              )}
            </Stack>
          </DialogContent>
          <DialogActions orientation="horizontal-reverse">
            <Button type="submit" loading={loading}>
              <Trans id="Save" />
            </Button>
            <Button variant="outlined" color="neutral" onClick={onClose} disabled={loading}>
              <Trans id="Cancel" />
            </Button>
          </DialogActions>
        </form>
      </ModalDialog>
    </Modal>
  );
};
